import authentication from '@feathersjs/authentication-client'
import * as errors from '@feathersjs/errors'
import { feathers } from '@feathersjs/feathers'
import rest, { FetchClient } from '@feathersjs/rest-client'
import { useLocalStorage } from '@vueuse/core'
import { createPiniaClient } from 'feathers-pinia'
import { DateTime } from 'luxon'
import { pinia } from 'src/modules/pinia'
import { decodeJwtOrNull, isJwtExpired, isJwtValid } from 'src/utils/jwt.js'
import { getAPIBaseURL, getStorageKey, isCordovaAndroid } from 'src/utils/utils.js'

const accessTokenStorageKey = getStorageKey('access')
const refreshTokenStorageKey = getStorageKey('refresh')

const accessToken = useLocalStorage(accessTokenStorageKey, '')
const refreshToken = useLocalStorage(refreshTokenStorageKey, '')
const loginRedirect = useLocalStorage('loginRedirect', '')
let refreshPromise = null

const isAuthenticationEndpoint = (urlObj) => urlObj.pathname.endsWith('/authentication/')

const logoutForInvalidAuthentication = async () => {
  const authStore = useAuthStore()
  if (isCordovaAndroid()) {
    await authStore.fullLogout(true)
  } else {
    await authStore.fullLogout(true, undefined, true)
  }
}

const refreshStoredTokens = async (connection = fetch) => {
  const authStore = useAuthStore()
  if (isJwtExpired(refreshToken.value, DateTime.now().toSeconds())) {
    await logoutForInvalidAuthentication()
    return false
  }

  const response = await connection(new URL('/v2/authentication/', process.env.API_HOSTNAME), {
    method: 'POST',
    body: JSON.stringify({
      strategy: 'refresh',
      refreshToken: refreshToken.value,
    }),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).catch(() => null)

  if (!response?.ok) {
    return false
  }

  const jsonResponse = await response.json().catch(() => null)
  if (!jsonResponse?.accessToken) {
    return false
  }

  await authStore.setAuthResponse(jsonResponse, false)
  return jsonResponse
}

const refreshStoredTokensOnce = (connection = fetch) => {
  if (!refreshPromise) {
    refreshPromise = refreshStoredTokens(connection).finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

const applyRefreshedTokensToRequest = (responseJson, fetchOptions, body = null) => {
  if (responseJson?.accessToken) {
    accessToken.value = responseJson.accessToken
    fetchOptions.headers['Authorization'] = `Bearer ${responseJson.accessToken}`
    if (body?.strategy === 'jwt') {
      body.accessToken = responseJson.accessToken
      fetchOptions.body = JSON.stringify(body)
    }
  }
  if (responseJson?.refreshToken) {
    refreshToken.value = responseJson.refreshToken
  }
}

// Provides access to Feathers Client(s)
export const checkAndRefreshTokens = async (currentAccessToken, connection = fetch) => {
  if (isJwtExpired(currentAccessToken, DateTime.now().toSeconds())) {
    return await refreshStoredTokensOnce(connection)
  }

  return true
}

export class CustomFetch extends FetchClient {
  checkStatus(response) {
    if (response.ok) {
      return response
    }
    return response
      .json()
      .catch((err) => {
        const ErrorClass = errors[response.status] || Error
        return new ErrorClass('JSON parsing error')
      })
      .then(async (error) => {
        if (error.code === 401) {
          accessToken.value = null
          if (error.error?.code !== 'INVALID_LOGIN') {
            window.location.href = '/'
          }
        } else if (error.code === 403) {
          if (error.error?.code === 'MFA_EXPIRED') {
            window.location.href = '/auth/verify-mfa'
          }
        }
        error.response = response
        throw error
      })
  }

  async request(options, params) {
    let urlObj
    let isRelative = false

    try {
      // Try constructing a URL assuming options.url is absolute.
      urlObj = new URL(options.url)
    } catch {
      // If options.url is relative, use a dummy base for parsing.
      isRelative = true
      urlObj = new URL(options.url, process.env.API_HOSTNAME)
    }

    // Ensure the pathname ends with a slash.
    if (!urlObj.pathname.endsWith('/')) {
      urlObj.pathname += '/'
    }

    // Reconstruct the URL.
    if (isRelative) {
      // If the original URL was relative, rebuild it without the dummy origin.
      options.url = urlObj.pathname + urlObj.search + urlObj.hash
    } else {
      // If the URL was absolute, use the full URL.
      options.url = urlObj.toString()
    }
    const shouldHandleAuthentication = !isAuthenticationEndpoint(urlObj)

    const fetchOptions = Object.assign({}, options, params.connection)
    fetchOptions.credentials = fetchOptions.credentials || 'include'

    fetchOptions.headers = Object.assign({ Accept: 'application/json' }, this.options.headers, fetchOptions.headers)

    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body)
    }

    // Hack to fix a hot-reload issue where it initially would send a request without any access token at all
    if (
      shouldHandleAuthentication &&
      !fetchOptions.headers['Authorization'] &&
      accessToken.value &&
      isJwtValid(accessToken.value, DateTime.now().toSeconds())
    ) {
      fetchOptions.headers['Authorization'] = `Bearer ${accessToken.value}`
    }

    // The header includes auth, check if the token needs refreshing first
    // This can be a request to the authentication endpoint using JWT strategy
    if (options.body?.strategy === 'jwt' && options.body?.accessToken) {
      const responseJson = await checkAndRefreshTokens(options.body?.accessToken, this.connection)
      if (responseJson !== true) {
        applyRefreshedTokensToRequest(responseJson, fetchOptions, options.body)
      }
    }
    // Or it can be that it was set as a Bearer token
    else if (fetchOptions.headers['Authorization']) {
      const authorizationHeader = fetchOptions.headers['Authorization']
      const accessTokenInAuthorizationHeader = typeof authorizationHeader === 'string' ? authorizationHeader.split(' ')[1] : null
      const responseJson = await checkAndRefreshTokens(accessTokenInAuthorizationHeader, this.connection)
      if (responseJson !== true) {
        applyRefreshedTokensToRequest(responseJson, fetchOptions)
      }
    }
    // Or it can be a request without an explicit Authorization header while storage still contains an expired access token.
    else if (shouldHandleAuthentication && accessToken.value) {
      const responseJson = await checkAndRefreshTokens(accessToken.value, this.connection)
      if (responseJson === true && isJwtValid(accessToken.value, DateTime.now().toSeconds())) {
        fetchOptions.headers['Authorization'] = `Bearer ${accessToken.value}`
      } else if (responseJson?.accessToken) {
        applyRefreshedTokensToRequest(responseJson, fetchOptions)
      }
    }

    let response = await this.connection(options.url, fetchOptions)
    if (response.status === 401 && shouldHandleAuthentication) {
      const responseJson = await refreshStoredTokensOnce(this.connection)
      if (responseJson?.accessToken) {
        applyRefreshedTokensToRequest(responseJson, fetchOptions)
        response = await this.connection(options.url, fetchOptions)
      }
    }

    return Promise.resolve(response)
      .then(this.checkStatus)
      .then((response) => {
        if (response.status === 204) {
          return null
        }

        const contentType = response.headers.get('Content-Type')

        if (contentType && contentType.includes('application/json')) {
          return response.json()
        } else {
          return response
        }
      })
  }
}

// First, parse out “access_token” and “refresh_token” from the query string
const params = new URLSearchParams(window.location.search)
const access = params.get('access_token')
const refresh = params.get('refresh_token')

if (decodeJwtOrNull(access)) {
  accessToken.value = access
}

if (decodeJwtOrNull(refresh)) {
  refreshToken.value = refresh
}

// Store a redirect URL
if (window.location.pathname !== '/' && !window.location.pathname.startsWith('/auth')) {
  loginRedirect.value = window.location.pathname
}

// Handle redirect (if you saved one previously)
if (loginRedirect.value !== '') {
  const redirect = loginRedirect.value
  loginRedirect.value = ''
  if (window.location.pathname !== redirect && redirect !== '') {
    window.location.pathname = redirect
  }
}

const reactiveStorage = {
  getItem(key) {
    if (key === accessTokenStorageKey) {
      return accessToken.value
    } else if (key === refreshTokenStorageKey) {
      return refreshToken.value
    }
    return null
  },
  setItem(key, val) {
    if (key === accessTokenStorageKey) {
      accessToken.value = val
    } else if (key === refreshTokenStorageKey) {
      refreshToken.value = val
    }
  },
  removeItem(key) {
    if (key === accessTokenStorageKey) {
      accessToken.value = null
    } else if (key === refreshTokenStorageKey) {
      refreshToken.value = null
    }
  },
}

const restClient = rest(getAPIBaseURL()).fetch(window.fetch.bind(window), CustomFetch)
export const feathersClient = feathers()
  .configure(restClient)
  .configure(
    authentication({
      scheme: 'Bearer',
      storageKey: accessTokenStorageKey,
    }),
  )

export const api = createPiniaClient(feathersClient, {
  pinia,
  idField: 'id',
  whitelist: [''],
  paramsForServer: [],
  storage: reactiveStorage,
  services: {
    user: {
      idField: 'id',
    },
    client: {
      skipGetIfExists: true,
    },
  },
})
